import React from "react";
import { Link, NavLink } from "react-router-dom";
import { useApp } from "@/contexts/AppContext";
import { useI18n } from "@/lib/i18n";
import { Button } from "@/components/ui/button";
import LanguageSwitcher from "./LanguageSwitcher";
import { Menu, X, LayoutDashboard } from "lucide-react";

export default function Header() {
    const { settings, user } = useApp();
    const { t } = useI18n();
    const [open, setOpen] = React.useState(false);

    const linkClass = ({ isActive }) =>
        `text-sm font-medium tracking-tight transition-colors ${isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"}`;

    const brand = settings.brand_name || "Bizlinkk";

    return (
        <header className="fixed top-0 inset-x-0 z-50 pt-3 sm:pt-5 px-3 sm:px-6">
            <div className="max-w-6xl mx-auto glass rounded-full">
                <div className="flex items-center justify-between h-14 pl-4 pr-2 sm:pl-6 sm:pr-3">
                    <Link to="/" className="flex items-center gap-2.5 group" data-testid="header-logo">
                        <div className="w-7 h-7 rounded-full sphere transition-transform duration-500 group-hover:scale-110" />
                        <span className="font-heading text-sm sm:text-base font-semibold uppercase tracking-[0.22em] text-foreground">{brand}</span>
                    </Link>

                    <nav className="hidden md:flex items-center gap-7">
                        <NavLink to="/" end className={linkClass} data-testid="nav-home-link">{t("nav_home")}</NavLink>
                        <a href="/#how" className="text-sm font-medium text-muted-foreground hover:text-foreground">{t("nav_how")}</a>
                        <NavLink to="/pricing" className={linkClass} data-testid="nav-pricing-link">{t("nav_pricing")}</NavLink>
                        <NavLink to="/b/royal-garments" className={linkClass} data-testid="nav-example-link">{t("nav_example")}</NavLink>
                    </nav>

                    <div className="flex items-center gap-2">
                        <LanguageSwitcher variant="ghost" testid="header-lang" />
                        {user ? (
                            <Link to="/dashboard" className="hidden sm:inline-flex">
                                <Button className="btn-accent rounded-full h-10 px-5 text-sm" data-testid="header-dashboard-btn">
                                    <LayoutDashboard className="w-4 h-4 mr-1.5" /> {t("dashboard")}
                                </Button>
                            </Link>
                        ) : (
                            <>
                                <Link to="/login" className="hidden sm:inline-flex">
                                    <Button variant="ghost" className="rounded-full h-10 px-4 text-sm" data-testid="header-login-btn">{t("login")}</Button>
                                </Link>
                                <Link to="/signup" className="hidden sm:inline-flex">
                                    <Button className="btn-accent rounded-full h-10 px-5 text-sm" data-testid="header-signup-btn">{t("create_website")}</Button>
                                </Link>
                            </>
                        )}
                        <button className="md:hidden w-10 h-10 flex items-center justify-center rounded-full border border-border/70 bg-white/50 transition-transform active:scale-95"
                            onClick={() => setOpen((o) => !o)} data-testid="mobile-menu-toggle">
                            {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
                        </button>
                    </div>
                </div>
                {open && (
                    <div className="md:hidden border-t border-border/70 px-6 py-4 flex flex-col gap-3">
                        <NavLink to="/" end className={linkClass} onClick={() => setOpen(false)}>{t("nav_home")}</NavLink>
                        <a href="/#how" className="text-sm text-muted-foreground" onClick={() => setOpen(false)}>{t("nav_how")}</a>
                        <NavLink to="/pricing" className={linkClass} onClick={() => setOpen(false)}>{t("nav_pricing")}</NavLink>
                        <NavLink to="/b/royal-garments" className={linkClass} onClick={() => setOpen(false)}>{t("nav_example")}</NavLink>
                        <div className="h-px bg-border/70 my-1" />
                        {user ? (
                            <Link to="/dashboard" onClick={() => setOpen(false)}>
                                <Button className="btn-accent rounded-full w-full" data-testid="mobile-dashboard-btn">{t("dashboard")}</Button>
                            </Link>
                        ) : (
                            <div className="flex flex-col gap-2">
                                <Link to="/login" onClick={() => setOpen(false)}><Button variant="ghost" className="rounded-full w-full">{t("login")}</Button></Link>
                                <Link to="/signup" onClick={() => setOpen(false)}><Button className="btn-accent rounded-full w-full">{t("create_website")}</Button></Link>
                            </div>
                        )}
                    </div>
                )}
            </div>
        </header>
    );
}
